/**
 * HermesChatPanel — Skerpa copilot panel with Chat / Suggestions / Activity tabs.
 */

import { useState, useEffect, useRef } from 'react'
import { Sparkles, X, Trash2, Maximize2, Minimize2, ChevronDown, Compass, PanelBottomOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHermesChat } from '@/hooks/useHermesChat'
import { useProjectStore } from '@/stores/projectStore'
import { useSkerpaStore } from '@/stores/skerpaStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { SKERPA_MODE_LABELS, SKERPA_MODE_DESCRIPTIONS } from '@ispot/shared'
import type { SkerpaMode } from '@ispot/shared'
import { SkerpaChatTab } from './SkerpaChatTab'
import { SkerpaSuggestionsTab } from './SkerpaSuggestionsTab'
import { SkerpaActivityTab } from './SkerpaActivityTab'
import type { Suggestion } from './SkerpaSuggestionCard'

const MODES: SkerpaMode[] = ['standard', 'plus', 'premium']
const MODE_SHORT: Record<SkerpaMode, string> = { standard: 'STD', plus: 'PLUS', premium: 'PRO' }

type TabId = 'chat' | 'suggestions' | 'activity'

interface HermesChatPanelProps {
  isOpen: boolean
  isFullscreen?: boolean
  onClose: () => void
  onViewChange?: (view: string) => void
  onToggleFullscreen?: () => void
  onSwitchToFloating?: () => void
  suggestions?: Suggestion[]
  onApplySuggestion?: (s: Suggestion) => Promise<void> | void
  onDismissSuggestion?: (s: Suggestion) => void
}

export function HermesChatPanel({
  isOpen, isFullscreen, onClose, onViewChange, onToggleFullscreen, onSwitchToFloating,
  suggestions = [], onApplySuggestion, onDismissSuggestion,
}: HermesChatPanelProps) {
  const { messages, clearHistory } = useHermesChat()
  const [activeTab, setActiveTab] = useState<TabId>('chat')
  const [showModeMenu, setShowModeMenu] = useState(false)
  const modeMenuRef = useRef<HTMLDivElement>(null)
  const currentProject = useProjectStore(s => s.currentProject)
  const skerpaMode = useSkerpaStore(s => s.skerpaMode)
  const setSkerpaMode = useSkerpaStore(s => s.setSkerpaMode)
  const selectedGoal = useJourneyStore(s => s.getSelectedGoal())
  const currentPhase = useJourneyStore(s => s.getCurrentPhase())

  useEffect(() => {
    if (!showModeMenu) return
    const handler = (e: MouseEvent) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) setShowModeMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showModeMenu])

  if (!isOpen) return null

  const suggestionCount = suggestions.length
  const activityCount = messages.length
  const modeColor = skerpaMode === 'premium'
    ? { bg: '#fbe7a1', fg: '#8a5a00' }
    : skerpaMode === 'plus' ? { bg: 'rgb(219 234 254)', fg: 'rgb(29 78 216)' }
    : { bg: 'var(--color-warm-200)', fg: 'var(--color-text-secondary)' }

  return (
    <div className={cn(
      'h-full border-l border-border bg-warm-50 flex flex-col flex-shrink-0 shadow-[-4px_0_12px_rgba(29,26,21,0.06)] transition-all duration-200',
      isFullscreen ? 'flex-1 min-w-0' : 'w-96',
    )}>
      {/* Header — Skerpa */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border-subtle bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-[18px] h-[18px] flex-shrink-0" style={{ color: 'var(--color-solution-700)' }} />
          <span className="text-[16px] font-semibold text-text truncate" style={{ fontFamily: 'var(--font-display)' }}>Skerpa</span>
          <div className="relative" ref={modeMenuRef}>
            <button
              onClick={() => setShowModeMenu(v => !v)}
              className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors tracking-wide"
              style={{ backgroundColor: modeColor.bg, color: modeColor.fg }}
            >
              {MODE_SHORT[skerpaMode]}
              <ChevronDown className="w-2.5 h-2.5" />
            </button>
            {showModeMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-border rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
                {MODES.map(m => (
                  <button
                    key={m}
                    onClick={() => { setSkerpaMode(m); setShowModeMenu(false) }}
                    className={cn('w-full text-left px-3 py-1.5 hover:bg-warm-50 transition-colors', m === skerpaMode && 'bg-warm-50')}
                  >
                    <div className="text-[12px] font-medium text-text">{SKERPA_MODE_LABELS[m]}</div>
                    <div className="text-[10px] text-text-muted">{SKERPA_MODE_DESCRIPTIONS[m]}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedGoal && (
            <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: '#fef9e7', color: '#a37a1a' }} title={currentProject?.name}>
              <Compass className="w-3 h-3" /><span className="truncate max-w-[100px]">{selectedGoal.name}</span>
            </span>
          )}
          {activeTab === 'chat' && messages.length > 0 && (
            <button onClick={clearHistory} className="p-1.5 rounded hover:bg-warm-200/60 transition-colors" title="Clear conversation">
              <Trash2 className="w-3.5 h-3.5 text-text-muted" />
            </button>
          )}
          {onSwitchToFloating && (
            <button onClick={onSwitchToFloating} className="p-1.5 rounded hover:bg-warm-200/60 transition-colors" title="Switch to floating bar">
              <PanelBottomOpen className="w-3.5 h-3.5 text-text-muted" />
            </button>
          )}
          {onToggleFullscreen && (
            <button onClick={onToggleFullscreen} className="p-1.5 rounded hover:bg-warm-200/60 transition-colors" title={isFullscreen ? 'Sidebar mode' : 'Full screen'}>
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 text-text-muted" /> : <Maximize2 className="w-3.5 h-3.5 text-text-muted" />}
            </button>
          )}
          <button onClick={onClose} className="p-1.5 rounded hover:bg-warm-200/60 transition-colors">
            <X className="w-3.5 h-3.5 text-text-muted" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-stretch gap-1 px-2 border-b border-border-subtle bg-white">
        <TabButton label="Chat" active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} />
        <TabButton label="Suggestions" count={suggestionCount} active={activeTab === 'suggestions'} onClick={() => setActiveTab('suggestions')} />
        <TabButton label="Activity" count={activityCount} active={activeTab === 'activity'} onClick={() => setActiveTab('activity')} />
      </div>

      {/* Phase tag strip — only on chat tab */}
      {activeTab === 'chat' && currentPhase && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 border-b border-border-subtle bg-warm-50/60">
          <Compass className="w-3 h-3 flex-shrink-0 text-text-muted" style={{ color: '#d4a82a' }} />
          <span className="text-[11px] text-text-muted">
            Phase {currentPhase.order} · <span className="text-text-secondary font-medium">{currentPhase.name}</span>
          </span>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'chat' && <SkerpaChatTab isOpen={isOpen} isFullscreen={isFullscreen} onViewChange={onViewChange} />}
      {activeTab === 'suggestions' && (
        <SkerpaSuggestionsTab suggestions={suggestions} onApply={onApplySuggestion} onDismiss={onDismissSuggestion} />
      )}
      {activeTab === 'activity' && <SkerpaActivityTab />}
    </div>
  )
}

function TabButton({ label, count, active, onClick }: {
  label: string; count?: number; active: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex items-center gap-1.5 px-3 pt-2.5 pb-2 text-[13px] transition-colors',
        active ? 'font-semibold text-text' : 'font-medium text-text-muted hover:text-text'
      )}
    >
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className="text-[11px] text-text-muted">{count}</span>
      )}
      {active && <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-text rounded-t-sm" />}
    </button>
  )
}
